/**
 * High-impact economic releases on the trade screen (economic calendar): a chip in the instrument header from
 * 60 minutes before a high-impact release that moves this market, and a short warning above Buy / Sell from 15
 * minutes before. Both go once the result is out or 5 minutes after the release.
 */
import { useEffect, useState } from 'react';
import type { EconEvent } from '@platform/shared';
import { Link } from '../lib/router';
import { loadCalendar, useCalendar } from '../stores/markets';
import { cx, Icon } from './ui';

const MIN = 60_000;

function useClock(ms: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(t);
  }, [ms]);
  return now;
}

/** The next high-impact release for `symbol` within `within` minutes (or under way, up to 5 minutes after). */
export function useEventFor(symbol: string, within: number): { e: EconEvent; mins: number } | null {
  const cal = useCalendar();
  const now = useClock(15_000);
  useEffect(() => {
    void loadCalendar();
  }, [now]);
  if (!cal?.available) return null;
  const e = cal.data.find((x) => x.impact === 3 && !x.allDay && x.actual === null && x.symbols.includes(symbol) && Date.parse(x.time) - now <= within * MIN && now - Date.parse(x.time) < 5 * MIN);
  return e ? { e, mins: Math.max(0, Math.round((Date.parse(e.time) - now) / MIN)) } : null;
}

const when = (mins: number) => (mins ? `in ${mins} min` : 'now');

export function EventChip({ symbol, className }: { symbol: string; className?: string }) {
  const hit = useEventFor(symbol, 60);
  if (!hit) return null;
  const { e, mins } = hit;
  return (
    <Link
      to={`/markets/calendar/${e.guide}`}
      title={`${e.currency} ${e.event}: a high-impact release for this market. Open the guide.`}
      className={cx('inline-flex items-center gap-1.5 rounded-full border border-warn bg-warn-bg px-2.5 py-1 text-xs font-semibold whitespace-nowrap text-fg no-underline', className)}
    >
      <Icon name="calendar" size={12} className="text-warn" />
      High-impact <span className="num text-warn">{when(mins)}</span> · {e.event}
      <span className="text-accent-fg">Guide →</span>
    </Link>
  );
}

export function EventWarning({ symbol }: { symbol: string }) {
  const hit = useEventFor(symbol, 15);
  if (!hit) return null;
  const { e, mins } = hit;
  return (
    <div role="note" className="flex gap-2 rounded-lg bg-warn-bg px-3 py-2 text-[12px] leading-snug">
      <Icon name="info" size={14} className="mt-px shrink-0 text-warn" />
      <span>
        <b className="block text-warn">
          {e.currency} {e.event} {when(mins)}
        </b>
        Prices can jump in seconds around the release. Spreads can widen and a stop can close past its price.{' '}
        <Link to={`/markets/calendar/${e.guide}`} className="text-accent-fg">
          Learn more
        </Link>
      </span>
    </div>
  );
}
